import { useTranslation } from "react-i18next";
import InfoTip from "../../../components/InfoTip";
import { ActivityMetricInfoIcon } from "../../../components/ActivityDetailMetricIcons";
import {
  ACTIVITY_KEY_MOMENT_CATALOG,
  ACTIVITY_KEY_MOMENT_THRESHOLDS,
} from "../utils/activityDetailAnalysis";

const KeyMomentBellIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className="shrink-0"
  >
    <path
      d="M1.97161 11.3333C1.78272 11.3333 1.62438 11.2694 1.49661 11.1417C1.36883 11.0139 1.30494 10.8556 1.30494 10.6667C1.30494 10.4778 1.36883 10.3194 1.49661 10.1917C1.62438 10.0639 1.78272 10 1.97161 10H2.63827V5.33333C2.63827 4.41111 2.91605 3.59167 3.47161 2.875C4.02716 2.15833 4.74938 1.68889 5.63827 1.46667V1C5.63827 0.722222 5.73549 0.486111 5.92994 0.291667C6.12438 0.0972222 6.36049 0 6.63827 0C6.91605 0 7.15216 0.0972222 7.34661 0.291667C7.54105 0.486111 7.63827 0.722222 7.63827 1V1.46667C8.52716 1.68889 9.24938 2.15833 9.80494 2.875C10.3605 3.59167 10.6383 4.41111 10.6383 5.33333V10H11.3049C11.4938 10 11.6522 10.0639 11.7799 10.1917C11.9077 10.3194 11.9716 10.4778 11.9716 10.6667C11.9716 10.8556 11.9077 11.0139 11.7799 11.1417C11.6522 11.2694 11.4938 11.3333 11.3049 11.3333H1.97161ZM6.63827 13.3333C6.27161 13.3333 5.95772 13.2028 5.69661 12.9417C5.43549 12.6806 5.30494 12.3667 5.30494 12H7.97161C7.97161 12.3667 7.84105 12.6806 7.57994 12.9417C7.31883 13.2028 7.00494 13.3333 6.63827 13.3333ZM3.97161 10H9.30494V5.33333C9.30494 4.6 9.04383 3.97222 8.52161 3.45C7.99938 2.92778 7.37161 2.66667 6.63827 2.66667C5.90494 2.66667 5.27716 2.92778 4.75494 3.45C4.23272 3.97222 3.97161 4.6 3.97161 5.33333V10ZM0.638272 5.33333C0.449383 5.33333 0.291049 5.26111 0.163272 5.11667C0.0354938 4.97222 -0.017284 4.80556 0.00493827 4.61667C0.0938272 3.78333 0.327161 3.00833 0.704938 2.29167C1.08272 1.575 1.56605 0.95 2.15494 0.416667C2.29938 0.294444 2.46327 0.238889 2.64661 0.25C2.82994 0.261111 2.97716 0.344444 3.08827 0.5C3.19938 0.655556 3.24383 0.822222 3.22161 1C3.19938 1.17778 3.11605 1.33333 2.97161 1.46667C2.53827 1.87778 2.18272 2.35556 1.90494 2.9C1.62716 3.44444 1.44383 4.03333 1.35494 4.66667C1.33272 4.85556 1.25494 5.01389 1.12161 5.14167C0.988272 5.26944 0.827161 5.33333 0.638272 5.33333ZM12.6383 5.33333C12.4494 5.33333 12.2883 5.26944 12.1549 5.14167C12.0216 5.01389 11.9438 4.85556 11.9216 4.66667C11.8327 4.03333 11.6494 3.44444 11.3716 2.9C11.0938 2.35556 10.7383 1.87778 10.3049 1.46667C10.1605 1.33333 10.0772 1.17778 10.0549 1C10.0327 0.822222 10.0772 0.655556 10.1883 0.5C10.2994 0.344444 10.4466 0.261111 10.6299 0.25C10.8133 0.238889 10.9772 0.294444 11.1216 0.416667C11.7105 0.95 12.1938 1.575 12.5716 2.29167C12.9494 3.00833 13.1827 3.78333 13.2716 4.61667C13.2938 4.80556 13.2411 4.97222 13.1133 5.11667C12.9855 5.26111 12.8272 5.33333 12.6383 5.33333Z"
      fill="#222222"
    />
  </svg>
);

const formatMomentDate = (time, language, t) => {
  const formatted = new Date(time).toLocaleDateString(language || "fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Europe/Paris",
  });
  return t("activityKeyMoment.date", { date: formatted });
};

const formatMomentTime = (time, language) =>
  new Date(time).toLocaleTimeString(language || "fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Paris",
  });

const KeyMomentsHelpTip = ({ t }) => (
  <InfoTip widthMaxRem={18} icon={<ActivityMetricInfoIcon />}>
    <p className="m-0 mb-2 text-[13px] font-semibold leading-tight">
      {t("activityKeyMomentGuide.title")}
    </p>
    <ul className="m-0 space-y-2 list-none p-0">
      {ACTIVITY_KEY_MOMENT_CATALOG.map((entry) => (
        <li key={entry.id} className="text-[13px] leading-snug">
          <span className="font-semibold">
            {t(entry.titleKey)}
            {" — "}
          </span>
          <span className="opacity-90">
            {t(entry.criterionKey, ACTIVITY_KEY_MOMENT_THRESHOLDS)}
          </span>
        </li>
      ))}
    </ul>
  </InfoTip>
);

export const ActivitySummaryKeyMoments = ({
  moments = [],
  selectedTime = null,
  onSelectMoment,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language || "fr-FR";

  return (
    <div className="flex flex-col min-h-0 h-full w-full">
      <div className="char-section-header char-section-header--general-info flex flex-row !justify-start items-center gap-2 !mb-3 min-w-0">
        <h2 className="char-section-header-title !mb-0">{t("Key moments")}</h2>
        <KeyMomentsHelpTip t={t} />
      </div>

      {moments.length === 0 ? (
        <p className="m-0 text-sm text-[#6B7280]">
          {t("activityDetailNoKeyMoments")}
        </p>
      ) : (
        <ul className="m-0 p-0 flex flex-col gap-2 list-none flex-1 min-h-0 overflow-y-auto pr-1">
          {moments.map((moment) => {
            const isSelected = selectedTime === moment.time;
            const timeLabel = formatMomentTime(moment.time, language);
            return (
              <li key={moment.id}>
                <button
                  type="button"
                  onClick={() => onSelectMoment?.(moment.time)}
                  className={`w-full text-left rounded-xl p-3 bg-white cursor-pointer box-border transition-shadow overflow-hidden ${
                    isSelected
                      ? "border-[3px] border-[#00A7A6]"
                      : "border border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <KeyMomentBellIcon />
                    <span className="font-bold text-sm text-[#222222]">
                      {formatMomentDate(moment.time, language, t)}
                    </span>
                  </div>
                  <p className="m-0 mt-1.5 text-sm text-gray-700 leading-snug">
                    {t(moment.descriptionKey, {
                      ...moment.descriptionParams,
                      time: timeLabel,
                    })}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
